import { createServerFn } from "@tanstack/react-start";

export type AnalysisType = "tldr" | "sentiment";

export type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

const LANGUAGE_NAMES: Record<string, string> = {
  en: "English", es: "Spanish", fr: "French", de: "German", pt: "Portuguese",
  ru: "Russian", zh: "Chinese", ja: "Japanese", ko: "Korean", ar: "Arabic",
  hi: "Hindi", it: "Italian", nl: "Dutch", tr: "Turkish", vi: "Vietnamese",
  th: "Thai", id: "Indonesian",
  no: "Norwegian (bokmal)", "zh-Hant": "Traditional Chinese, never simplified characters",
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface AnalyzeArticleArgs {
  articleId: string;
  analysisType: AnalysisType;
  languageCode?: string;
}

export interface AnalyzeArticleResult {
  success: boolean;
  analysis?: Json;
  error?: string;
  rateLimited?: boolean;
}

/**
 * Cached analyses are public. Generating a new one calls a billed AI API, so
 * it requires a signed-in caller.
 */
export const analyzeArticle = createServerFn({ method: "POST" })
  .inputValidator((input: AnalyzeArticleArgs) => {
    if (!UUID_RE.test(input?.articleId ?? "")) throw new Error("Invalid article id");
    if (input.analysisType !== "tldr" && input.analysisType !== "sentiment") {
      throw new Error("Invalid analysis type");
    }
    const languageCode = input.languageCode && LANGUAGE_NAMES[input.languageCode] ? input.languageCode : "en";
    return { articleId: input.articleId, analysisType: input.analysisType, languageCode };
  })
  .handler(async ({ data }): Promise<AnalyzeArticleResult> => {
    const { articleId, analysisType, languageCode } = data;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { geminiChat, NO_EM_DASH_RULE } = await import("./ai.server");
    const { stripEmDashesDeep } = await import("./text");

    const analysisTypeKey = languageCode === "en" ? analysisType : `${analysisType}_${languageCode}`;

    const { data: cached } = await supabaseAdmin
      .from("article_analysis")
      .select("analysis_data")
      .eq("article_id", articleId)
      .eq("analysis_type", analysisTypeKey)
      .maybeSingle();

    if (cached) return { success: true, analysis: cached.analysis_data as Json };

    // Generation is open to everyone: results are cached per article + type +
    // language with ignoreDuplicates, so each combination is only billed once.

    const { data: article } = await supabaseAdmin
      .from("articles")
      .select("title, content, excerpt")
      .eq("id", articleId)
      .eq("is_published", true)
      .maybeSingle();

    // Stored bodies are HTML; the AI reads plain text and the length check must
    // measure real words, not tags.
    const toPlainText = (value: string) =>
      value
        .replace(/<script[\s\S]*?<\/script>/gi, " ")
        .replace(/<style[\s\S]*?<\/style>/gi, " ")
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, " ")
        .trim();

    const rawBody = article?.content || article?.excerpt || "";
    const content = toPlainText(rawBody);
    const title = article?.title?.trim();
    if (!article || !title || content.length < 80) {
      return { success: false, error: "Published article content is unavailable" };
    }

    const languageName = LANGUAGE_NAMES[languageCode] ?? "English";
    const languageInstruction =
      languageCode !== "en"
        ? `\n\nIMPORTANT: Respond entirely in ${languageName}. All JSON string values MUST be written in ${languageName}.`
        : "";

    let systemPrompt: string;
    let prompt: string;

    if (analysisType === "tldr") {
      systemPrompt = `You are an expert analyst covering disruptive technology, finance, business, policy, and science. Create clear, concise summaries and critical analysis without assuming the subject is cryptocurrency. IMPORTANT: Return ONLY valid JSON without markdown formatting or code blocks.${languageInstruction}`;
      prompt = `Analyze this article and provide:

1. TLDR (2-3 sentences max): the most important takeaway
2. Key Points (3-5 bullet points): main facts and developments
3. Simplified (2-3 sentences): explain complex concepts in simple terms
4. Challenge This (2-3 sentences): potential counterarguments or risks

Article Title: ${title}
Article Content: ${content.substring(0, 4000)}

Return ONLY this JSON object, under 250 words total.
{
  "tldr": "...",
  "keyPoints": ["...", "...", "..."],
  "simplified": "...",
  "challengeThis": "..."
}`;
    } else {
      systemPrompt = `You are a technology, business, and market sentiment analyst. Analyze the emotional tone and implications of articles without assuming the subject is cryptocurrency. IMPORTANT: Return ONLY valid JSON without markdown formatting or code blocks.${languageInstruction}`;
      prompt = `Analyze the sentiment of this article.

Article Title: ${title}
Article Content: ${content.substring(0, 4000)}

Return ONLY this JSON object, under 250 words total.
{
  "sentiment": "BULLISH|BEARISH|NEUTRAL",
  "confidence": 85,
  "sentimentDrivers": ["phrase1", "phrase2", "phrase3"],
  "marketImplications": "...",
  "emotionalTone": "..."
}`;
    }

    let raw: string;
    try {
      raw = await geminiChat(
        [
          { role: "system", content: systemPrompt + NO_EM_DASH_RULE },
          { role: "user", content: prompt },
        ],
        { temperature: 0.3, maxTokens: 3000, responseFormat: { type: "json_object" } },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Analysis unavailable";
      const lower = message.toLowerCase();
      const rateLimited =
        message.includes("429") || lower.includes("rate limit") || lower.includes("temporarily unavailable");
      return {
        success: false,
        rateLimited,
        error: rateLimited ? "AI is busy right now. Please try again in a minute." : message,
      };
    }

    const block = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    const cleaned = (block?.[1] ?? raw).trim();

    let parsed: Json;
    try {
      parsed = stripEmDashesDeep(JSON.parse(cleaned)) as Json;
    } catch {
      return { success: false, error: "Invalid AI response format" };
    }

    await supabaseAdmin.from("article_analysis").upsert(
      {
        article_id: articleId,
        analysis_type: analysisTypeKey,
        analysis_data: parsed as never,
      },
      { onConflict: "article_id,analysis_type", ignoreDuplicates: true },
    );

    return { success: true, analysis: parsed };
  });

export interface ScrapeArticleResult {
  success: boolean;
  content?: string;
  skipped?: boolean;
  status?: string;
  via?: string;
  length?: number;
  message?: string;
  error?: string;
}

const PLACEHOLDER_HINTS = ["placeholder", "unsplash.com/photo-1518546305927", "default-"];
const isPlaceholderImage = (url?: string | null) =>
  !url || PLACEHOLDER_HINTS.some((hint) => url.toLowerCase().includes(hint));

/** Fetches the full body of a stored article. Signed-in callers only. */
export const scrapeArticle = createServerFn({ method: "POST" })
  .inputValidator((input: { articleId: string; sourceName?: string; force?: boolean }) => {
    if (!UUID_RE.test(input?.articleId ?? "")) throw new Error("Invalid article id");
    return {
      articleId: input.articleId,
      sourceName: input.sourceName ?? "",
      force: Boolean(input.force),
    };
  })
  .handler(async ({ data }): Promise<ScrapeArticleResult> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { getOptionalUserId } = await import("./authz.server");
    const { extractFullText, readTimeFor, stripHtmlTags, excerptFrom, isSafePublicUrl, MIN_ARTICLE_TEXT } =
      await import("./fullText.server");
    const { cleanText } = await import("./text");

    const userId = await getOptionalUserId();
    if (!userId) return { success: false, error: "Sign in to load the full story" };

    const { data: article } = await supabaseAdmin
      .from("articles")
      .select("id, url, title, excerpt, content, image_url, source_name, content_status")
      .eq("id", data.articleId)
      .maybeSingle();

    if (!article) return { success: false, error: "Article not found" };

    const url = article.url ?? "";
    if (!url || !isSafePublicUrl(url)) {
      return { success: false, error: "Article URL is not fetchable" };
    }

    const label = data.sourceName || article.source_name || "source";
    const existingLength = (article.content || "").length;

    if (!data.force && existingLength > 1500 && article.content_status === "full") {
      return { success: true, skipped: true, length: existingLength };
    }

    const result = await extractFullText(url);

    if (!result.html || result.text.length < MIN_ARTICLE_TEXT || result.html.length <= existingLength) {
      const update: Record<string, unknown> = {
        content_status: result.html && result.html.length <= existingLength ? "full" : "failed",
        content_fetched_at: new Date().toISOString(),
      };
      if (result.imageUrl && isPlaceholderImage(article.image_url)) update["image_url"] = result.imageUrl;
      await supabaseAdmin.from("articles").update(update as never).eq("id", data.articleId);
      return {
        success: false,
        status: String(update["content_status"]),
        error: `Could not extract the full article from ${label}`,
      };
    }

    const plain = result.text;
    const update: Record<string, unknown> = {
      content: result.html,
      read_time: readTimeFor(plain),
      content_status: "full",
      content_fetched_at: new Date().toISOString(),
    };
    if (!article.excerpt || stripHtmlTags(article.excerpt).length < 40) {
      update["excerpt"] = cleanText(excerptFrom(plain));
    }
    if (result.imageUrl && isPlaceholderImage(article.image_url)) {
      update["image_url"] = result.imageUrl;
    }

    const { error: updateError } = await supabaseAdmin
      .from("articles")
      .update(update as never)
      .eq("id", data.articleId);
    if (updateError) return { success: false, error: updateError.message };

    return {
      success: true,
      content: result.html,
      via: result.via,
      length: result.html.length,
      message: `Full article extracted for ${label} (${plain.length} chars)`,
    };
  });
